/**
 * Link de cada fuente de datos a **su panel de administración** en el sistema de
 * origen: donde se ve si la integración está sana y donde se saca la credencial
 * que falta.
 *
 * Los builders del contrato apuntan a un registro (un work item, un lead, un
 * customer). Una fuente de datos no es un registro, es la integración entera, y
 * por eso su link se arma acá y no en `@ops/contracts`. Se mantiene toda la
 * regla: es puro, devuelve `null` cuando falta un dato de config y la UI
 * renderiza "sin vincular" en vez de un link roto.
 *
 * ---------------------------------------------------------------------------
 * EXCEPCIÓN RATIFICADA a "nunca al home ni a una búsqueda"
 * ---------------------------------------------------------------------------
 *
 * Estas cuatro URLs no apuntan a un registro y **está bien que no lo hagan**:
 *
 *  - `dashboard.stripe.com/subscriptions`
 *  - `app.plane.so/{workspace}/projects/{proyecto}/issues`
 *  - `app.laburen.com/clientes`
 *  - `search.google.com/search-console/performance/search-analytics?resource_id=…`
 *
 * La regla existe para que una fila que promete abrir "el lead de Armytech" abra
 * el lead de Armytech y no un buscador donde hay que encontrarlo a mano. Acá la
 * entidad de la fila **es la integración**, y una integración no tiene registro:
 * lo más específico que existe de "la fuente Stripe" es su listado de
 * suscripciones. Apuntar a la página de settings de la app —la alternativa que
 * levantó la verificación— sería *menos* preciso, no más: mandaría a la config
 * de la cuenta entera en vez de a los datos que esta fuente alimenta.
 *
 * Lo que sí se sostiene es que **el texto del link lo diga**. Ninguno promete un
 * registro: "Las suscripciones en Stripe", "El proyecto de seguimientos en
 * Plane", "Los clientes en la plataforma", "La propiedad en Search Console".
 * Ese es el contrato de esta excepción, y por eso los labels no son decorativos:
 * si alguno pasa a hablar en singular de una entidad concreta, el link tiene que
 * dejar de ser un listado. Ninguna otra parte del panel usa estos links.
 *
 * Las tres URLs sin parámetros no llevan filtro **a propósito**: un filtro
 * incompleto (una búsqueda) es exactamente lo que la regla prohíbe, mientras que
 * un listado completo es una página estable que no miente sobre qué muestra. La
 * de Search Console sí lleva `resource_id` porque sin él ni siquiera se sabe de
 * qué propiedad se habla; no lleva `query` porque la fuente no es una query.
 */
import {
  SYSTEM_LABELS,
  type DeepLink,
  type DeepLinkConfig,
  type SourceKind,
  type SystemOfRecord,
} from '@ops/contracts'

export interface ConsolaDeFuente {
  /** `null` = no se puede armar el link (o la fuente no es un sistema externo). */
  link: DeepLink | null
  /** Sistema al que apunta. `null` cuando la fuente es la carga manual. */
  sistema: SystemOfRecord | null
  /** Nombre legible del sistema, para el estado "sin vincular". */
  sistemaLabel: string | null
  /** Variable de entorno que falta para poder armar el link. */
  faltante: string | null
}

/** La carga manual no tiene consola externa: la consola es este panel. */
const SISTEMA_DE_FUENTE: Record<SourceKind, SystemOfRecord | null> = {
  manual: null,
  stripe: 'stripe',
  plane: 'plane',
  plataforma: 'plataforma',
  search_console: 'search_console',
  langfuse: 'langfuse',
}

function limpio(valor: string | null | undefined): string | null {
  if (valor === null || valor === undefined) return null
  const texto = valor.trim()
  return texto.length === 0 ? null : texto
}

/**
 * La base del sistema sin barra final, o `null` si no hay ninguna. Una base
 * vacía daba `/subscriptions`, una URL relativa que parece un link externo y en
 * realidad navega adentro del panel: se trata igual que un id ausente.
 */
function sinBarra(base: string | null | undefined): string | null {
  const texto = limpio(base)
  return texto === null ? null : texto.replace(/\/+$/, '')
}

const SIN_CONSOLA: ConsolaDeFuente = {
  link: null,
  sistema: null,
  sistemaLabel: null,
  faltante: null,
}

export function consolaDeFuente(
  cfg: DeepLinkConfig,
  fuente: SourceKind,
): ConsolaDeFuente {
  const sistema = SISTEMA_DE_FUENTE[fuente]
  if (sistema === null) return SIN_CONSOLA

  const sistemaLabel = SYSTEM_LABELS[sistema]
  const armado = (url: string, label: string): ConsolaDeFuente => ({
    link: { system: sistema, url, label, systemLabel: sistemaLabel },
    sistema,
    sistemaLabel,
    faltante: null,
  })
  const falta = (envVar: string): ConsolaDeFuente => ({
    link: null,
    sistema,
    sistemaLabel,
    faltante: envVar,
  })

  switch (sistema) {
    case 'stripe': {
      const raiz = sinBarra(cfg.stripe.baseUrl)
      if (raiz === null) return falta('STRIPE_BASE_URL')
      // Listado, no registro: ver la excepción ratificada en la cabecera.
      return armado(`${raiz}/subscriptions`, 'Las suscripciones en Stripe')
    }

    case 'plane': {
      const raiz = sinBarra(cfg.plane.baseUrl)
      if (raiz === null) return falta('PLANE_BASE_URL')
      const workspace = limpio(cfg.plane.workspaceSlug)
      if (workspace === null) return falta('PLANE_WORKSPACE_SLUG')
      const proyecto = limpio(cfg.plane.projectId)
      if (proyecto === null) return falta('PLANE_PROJECT_ID')
      return armado(
        `${raiz}/${encodeURIComponent(workspace)}/projects/${encodeURIComponent(proyecto)}/issues`,
        'El proyecto de seguimientos en Plane',
      )
    }

    case 'plataforma': {
      const raiz = sinBarra(cfg.plataforma.baseUrl)
      if (raiz === null) return falta('PLATAFORMA_BASE_URL')
      return armado(`${raiz}/clientes`, 'Los clientes en la plataforma')
    }

    case 'search_console': {
      const raiz = sinBarra(cfg.searchConsole.baseUrl)
      if (raiz === null) return falta('SEARCH_CONSOLE_BASE_URL')
      const recurso = limpio(cfg.searchConsole.resourceId)
      if (recurso === null) return falta('SEARCH_CONSOLE_RESOURCE_ID')
      const params = new URLSearchParams({ resource_id: recurso })
      return armado(
        `${raiz}/performance/search-analytics?${params.toString()}`,
        'La propiedad en Search Console',
      )
    }

    // Hoy siempre devuelve `falta`: las dos variables están vacías a propósito
    // mientras no se confirme si la instancia es cloud o self-hosted. Es el
    // estado correcto, no un defecto de esta función.
    case 'langfuse': {
      const raiz = sinBarra(cfg.langfuse.baseUrl)
      if (raiz === null) return falta('LANGFUSE_BASE_URL')
      const proyecto = limpio(cfg.langfuse.projectId)
      if (proyecto === null) return falta('LANGFUSE_PROJECT_ID')
      return armado(
        `${raiz}/project/${encodeURIComponent(proyecto)}`,
        'El proyecto en Langfuse',
      )
    }

    // Close no alimenta ninguna métrica: es fuente de leads, no de datapoints.
    default:
      return SIN_CONSOLA
  }
}
